import React, { useEffect, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { lifestyleApi } from "../apiService/api";
import { getUrlParams } from "../apiService/timespam";
import SectionHeader from "../Components/SectionHeader";
import { navigateWithFai, triggerShare } from "../utils/Utils";

// Shimmer helpers (same pattern as other lifestyle cards)
const ensureShimmerKeyframes = () => {
  if (typeof document === "undefined") return;
  if (document.getElementById("shimmer-keyframes")) return;

  const style = document.createElement("style");
  style.id = "shimmer-keyframes";
  style.innerHTML = `
    @keyframes shimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
  `;
  document.head.appendChild(style);
};

const shimmerBaseStyle = {
  position: "relative",
  overflow: "hidden",
  backgroundImage:
    "linear-gradient(90deg, var(--shimmer-1) 0%, var(--shimmer-2) 50%, var(--shimmer-1) 100%)",
  backgroundSize: "200% 100%",
  animation: "shimmer 1.3s linear infinite",
};

const StepgraphSkeleton = () => {
  useEffect(() => {
    ensureShimmerKeyframes();
  }, []);

  return (
    <div
      style={{
        width: "100%",
        backgroundColor: "var(--bg-card)",
        borderRadius: "20px",
        border: "1px solid var(--border-subtle)",
      }}
    >
      {/* SectionHeader skeleton */}
      <div style={{ padding: "12px 16px 0 16px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          {/* Icon */}
          <div
            style={{
              ...shimmerBaseStyle,
              width: 32,
              height: 32,
              borderRadius: 999,
            }}
          />

          {/* Title + subtitle */}
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            <div
              style={{
                ...shimmerBaseStyle,
                height: 18,
                width: "60%",
                borderRadius: 8,
              }}
            />
            <div
              style={{
                ...shimmerBaseStyle,
                height: 14,
                width: "40%",
                borderRadius: 8,
              }}
            />
          </div>

          {/* Info icon */}
          <div
            style={{
              ...shimmerBaseStyle,
              width: 20,
              height: 20,
              borderRadius: 999,
            }}
          />
        </div>
      </div>

      {/* Chart skeleton */}
      <div
        style={{
          marginTop: 12,
          padding: "0 16px 12px 16px",
        }}
      >
        <div
          style={{
            ...shimmerBaseStyle,
            height: 170,
            borderRadius: 16,
          }}
        />
      </div>

      {/* Footer text skeleton */}
      <div
        style={{
          padding: "10px 16px 16px 16px",
        }}
      >
        <div
          style={{
            ...shimmerBaseStyle,
            height: 16,
            width: "70%",
            borderRadius: 8,
          }}
        />
      </div>
    </div>
  );
};

const Stepgraph = ({ shareConfig } = {}) => {
  const [chartData, setChartData] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const { encryptedId } = getUrlParams();
    if (!encryptedId) return;

    lifestyleApi
      .stepgraphData(encryptedId)
      .then((res) => {
        if (res?.data?.code === 200) {
          setChartData(res.data.data);
        }
      })
      .catch(console.error);
  }, []);

  if (!chartData) return <StepgraphSkeleton />;


  const isSmallMobile = windowWidth < 420;
  const isMobile = windowWidth < 600;
  const isTablet = windowWidth < 900;

  const chartHeight = isSmallMobile ? 130 : isMobile ? 150 : 170;
  const labelFont = isSmallMobile ? "10px" : isMobile ? "11px" : "12px";
  const spaceLeft = isSmallMobile ? 10 : 20;
  const barPadding = isSmallMobile ? 0.25 : 0.15;
  const barGroupPadding = isSmallMobile ? 0.1 : 0.1;
  const dataLabelYOffset = isSmallMobile ? -5 : -10;

  const options = {
    chart: {
      type: "column",
      backgroundColor: "transparent",
      height: chartHeight,
      marginTop: 0,
      marginBottom: 20,
      spacingTop: 0,
      spacingBottom: 0,
      spacingLeft: spaceLeft,
      spacingRight: spaceLeft,

      style: { fontFamily: "Open Sans, sans-serif" },
      events: {
        click() {
          this.tooltip?.hide();
        },
        touchend() {
          this.tooltip?.hide();
        }
      }


    },


    title: { text: null },

    tooltip: { enabled: false },

    xAxis: {
      categories: chartData.xAxis,
      lineWidth: 1,
      lineColor: "var(--border-subtle)",
      tickLength: 0,
      gridLineWidth: 0,

      labels: {
        y: isSmallMobile ? 10 : 12,
        style: {
          fontSize: labelFont,
          fontWeight: "500",
          fontFamily: "Open Sans, sans-serif",
          color: "var(--text-primary)",
        },
      },
    },

    yAxis: { visible: false },

    plotOptions: {
      column: {
        borderRadius: 2,
        borderWidth: 0,
        borderColor: "transparent",
        pointPadding: barPadding,
        groupPadding: barGroupPadding,

        dataLabels: {
          enabled: true,
          inside: false,
          y: dataLabelYOffset,
          formatter() {
            return this.y;
          },
          style: {
            fontSize: labelFont,
            fontWeight: "bold",
            fontFamily: "Open Sans, sans-serif",
            textOutline: "none",
            color: "var(--text-primary)",
          },
        },
      },
    },

    series: [
      {
        showInLegend: false,
        color: "#D8A30A",
        data: chartData.yAxis,
      },
    ],

    credits: { enabled: false },
  };

  return (
    <div style={{ width: "100%", backgroundColor: "var(--bg-card)", borderRadius: "20px", border: "1px solid var(--border-subtle)" }}>
      <SectionHeader
        icon="https://appcdn.goqii.com/storeimg/42342_1764052750.png"
        title={chartData.avgSteps}
        subtitle={chartData.heading}
        infoIconLight="https://appcdn.goqii.com/storeimg/38298_1764052060.png"
        infoIconDark="https://appcdn.goqii.com/storeimg/26151_1769593659.png"
        showInfo
        onInfoClick={() => {
          navigateWithFai("Distance", chartData.infoUrl);
        }}
        showShare={!!shareConfig?.isSharable}
        onShareClick={() => triggerShare(shareConfig?.text)}
      />

      <HighchartsReact highcharts={Highcharts} options={options} />

      <div
        style={{
          padding: isSmallMobile ? "10px 15px" : "15px 20px",
          fontSize: "9px",
          fontFamily: "Open Sans, sans-serif",
          color: "var(--text-secondary)",
          textAlign: "left",
        }}
      >
        {chartData.monthText}
      </div>
    </div>
  );
};

export default Stepgraph;

